import { useEffect, useState } from "react";
import { MenuItem } from "@/components/MenuItem";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fetchMenu } from "@/lib/api";

type ApiMenuItem = {
  id: number;
  name: string;
  description: string;
  price: string;
  unit?: string;
};

type ApiCategory = {
  id: number;
  name: string;
  items: ApiMenuItem[];
};

export function Menu() {
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMenu()
      .then(setCategories)
      .catch(() => setError("Couldn't load the menu right now. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="mx-auto max-w-7xl px-6 py-20 space-y-16">
      <div className="text-center max-w-2xl mx-auto space-y-4">
        <h1 className="font-serif text-5xl text-foreground">Our Menu</h1>
        <div className="h-px w-24 bg-primary/60 mx-auto" />
        <p className="text-lg text-muted-foreground">
          Everything is baked fresh to order. Reach out to plan your pickup.
        </p>
      </div>

      {loading && (
        <p className="text-center text-muted-foreground">Loading menu...</p>
      )}

      {error && (
        <p className="text-center text-red-400">{error}</p>
      )}

      {!loading && !error && (
        <div className="grid gap-8 lg:grid-cols-2">
          {categories.map((category) => (
            <Card
              key={category.id}
              className="bg-card/50 backdrop-blur-sm border-white/5 hover:border-primary/20 transition-all duration-300"
            >
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-foreground">
                  {category.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {category.items.map((item) => (
                  <MenuItem
                    key={item.id}
                    name={item.name}
                    description={item.description}
                    price={item.price}
                    unit={item.unit}
                  />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
